import { useMemo, useState, useCallback } from 'react';
import {
  LayoutProps,
  rankWith,
  JsonSchema,
  createDefaultValue,
  update as updateDataAction,
  composePaths,
  ControlElement,
} from '@jsonforms/core';
import { JsonFormsDispatch, useJsonForms, withJsonFormsLayoutProps } from '@jsonforms/react';
import { Autocomplete, TextField, Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/** Custom UI schema type for the "add optional field" block. Must match the type used in buildRequiredPlusOptionalUISchema. */
export const OPTIONAL_FIELDS_ADDER_TYPE = 'OptionalFieldsAdder';

function uiTypeIs(type: string) {
  return (uischema: { type?: string }) => uischema?.type === type;
}

const OptionalFieldsAdderComponent = ({
  schema,
  path,
  visible,
  renderers,
  cells,
  data,
}: LayoutProps) => {
  const jsonforms = useJsonForms();
  const { dispatch } = jsonforms;

  const [addedKeys, setAddedKeys] = useState<string[]>([]);
  const [autocompleteValue, setAutocompleteValue] = useState<{ id: string; label: string } | null>(null);
  const [inputValue, setInputValue] = useState('');

  const props = schema?.properties as Record<string, JsonSchema> | undefined;
  const required = (schema?.required as string[] | undefined) ?? [];
  const optionalKeys = useMemo(
    () => (props ? Object.keys(props).filter((k) => !required.includes(k)) : []),
    [props, required]
  );

  // Show optional fields that are in data (loaded) or were added by the user
  const keysInData = useMemo(
    () => Object.keys(data ?? {}).filter((k) => optionalKeys.includes(k)),
    [data, optionalKeys]
  );
  const visibleOptionalKeys = useMemo(
    () => [...new Set([...addedKeys, ...keysInData])],
    [addedKeys, keysInData]
  );

  const options = useMemo(
    () =>
      optionalKeys.map((key) => {
        const s = props?.[key];
        const label = (s?.title as string) ?? key;
        return { id: key, label };
      }),
    [optionalKeys, props]
  );

  const availableOptions = useMemo(
    () => options.filter((o) => !visibleOptionalKeys.includes(o.id)),
    [options, visibleOptionalKeys]
  );

  const handleAddOptional = useCallback(
    (_: unknown, value: { id: string; label: string } | null) => {
      if (!value || !dispatch) return;
      const key = value.id;
      const propSchema = props?.[key];
      if (!propSchema) return;
      const childPath = composePaths(path, key);
      const defaultValue = createDefaultValue(propSchema, schema);
      dispatch(updateDataAction(childPath, () => defaultValue));
      setAddedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
      setAutocompleteValue(null);
      setInputValue('');
    },
    [dispatch, path, props, schema]
  );

  const handleRemoveOptional = useCallback(
    (key: string) => {
      if (!dispatch) return;
      dispatch(
        updateDataAction(path, (data: Record<string, unknown>) => {
          const next = { ...(data ?? {}) };
          delete next[key];
          return next;
        })
      );
      setAddedKeys((prev) => prev.filter((k) => k !== key));
    },
    [dispatch, path]
  );

  if (!visible) return null;
  if (optionalKeys.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Optional fields
      </Typography>
      <Autocomplete<{ id: string; label: string }>
        size="small"
        value={autocompleteValue}
        inputValue={inputValue}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        onChange={handleAddOptional}
        options={availableOptions}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.label ?? '')}
        renderInput={(params) => (
          <TextField {...params} label="Add optional field" placeholder="Choose a field…" />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.label}
          </li>
        )}
      />
      {visibleOptionalKeys.map((key) => {
        const propSchema = props?.[key];
        if (!propSchema) return null;
        const label = (propSchema.title as string) ?? key;

        // Create a mini-schema that wraps just this one property
        // This ensures JSON Forms can correctly resolve the scope
        const fieldSchema = {
          type: 'object',
          properties: {
            [key]: propSchema,
          },
        } as JsonSchema;

        const controlUischema: ControlElement = {
          type: 'Control',
          scope: `#/properties/${key}`,
          label,
        };
        const fieldPath = path; // Use parent path, not child path
        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mt: 1,
              '& > div:first-of-type': { flex: 1 },
            }}
          >
            <JsonFormsDispatch
              uischema={controlUischema}
              schema={fieldSchema}
              path={fieldPath}
              renderers={renderers}
              cells={cells}
              enabled={true}
              visible={true}
            />
            <IconButton
              size="small"
              aria-label={`Remove ${label}`}
              onClick={() => handleRemoveOptional(key)}
              sx={{ mt: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
};

export const optionalFieldsAdderTester = rankWith(2, uiTypeIs(OPTIONAL_FIELDS_ADDER_TYPE));

export default withJsonFormsLayoutProps(OptionalFieldsAdderComponent);
